import { useMemo, useState } from 'react'
import { AlertTriangle, Check, Info, X } from 'lucide-react'
import { useAdminOrgsProvisioning } from '../../api/admin-phones'
import {
  getFlipErrorMessage,
  parseInFlightCallsError,
  useFlipOrgProvider,
  type VoiceProvider,
} from '../../api/admin-provider'

/**
 * Voice-provider flip control — superadmin-only sibling of the Retell phone
 * inventory in :file:`AdminBillingPhonesTab.tsx`. Wraps
 * ``POST /api/admin/orgs/{org_id}/provider`` (PR #116) with a browser
 * affordance so an operator can flip an org between ``vapi`` and ``retell``
 * with a click + confirmation instead of curl.
 *
 * ── Where the current provider comes from ────────────────────────────────────
 * The badge reads the org's saved ``provider`` from ``orgs-provisioning``
 * (surfaced by phone_assignments.py), so it shows the correct value on load
 * and survives a refresh. A flip confirmed THIS session takes precedence over
 * the fetched value (it's the most current), keyed by ``orgId`` so switching
 * the selector doesn't bleed one org's result onto another. The badge only
 * reads null in the unlikely case the server omits the field.
 *
 * The flip itself is always safe regardless of what we display: the backend
 * validates the target, refuses mid-call flips (409), and the operator
 * confirms the exact target provider in the modal before it fires.
 *
 * Step-up MFA, if the backend later requires it on this route, is handled
 * centrally in :file:`api/client.ts` — a 401 ``step_up_required`` bounces the
 * browser through ``/auth/workos/step-up`` and back. (The endpoint is
 * superadmin-only today and does NOT gate on step-up, unlike the recordings
 * delete — but the client handles it transparently either way.)
 */
export function OrgProviderControl() {
  const orgs = useAdminOrgsProvisioning()
  const [orgId, setOrgId] = useState<string>('')

  // Provider confirmed via a flip THIS session, keyed by org so switching the
  // selector never shows a stale value from a different org. Absent ⇒ unknown.
  const [knownProvider, setKnownProvider] = useState<
    Record<string, VoiceProvider>
  >({})

  // Stable alpha sort for the dropdown — matches RetellPhoneInventorySection.
  const sortedOrgs = useMemo(
    () =>
      [...(orgs.data?.orgs ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [orgs.data?.orgs],
  )

  const selectedOrg = sortedOrgs.find((o) => o.org_id === orgId) ?? null

  return (
    <section className="overflow-hidden rounded-lg border border-border bg-surface">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3">
        <div>
          <div className="font-medium">Voice provider</div>
          <div className="text-xs text-text-secondary">
            Flip which provider answers an org&rsquo;s calls between{' '}
            <code>vapi</code> and <code>retell</code>. In-flight calls finish on
            their current provider.
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <span className="text-text-secondary">Org</span>
          <select
            value={orgId}
            onChange={(e) => setOrgId(e.target.value)}
            className="rounded border border-border bg-background px-2 py-1 text-sm"
            disabled={orgs.isLoading}
          >
            <option value="">
              {orgs.isLoading ? 'Loading orgs…' : 'Pick an org…'}
            </option>
            {sortedOrgs.map((o) => (
              <option key={o.org_id} value={o.org_id}>
                {o.name}
                {o.slug ? ` · ${o.slug}` : ''}
              </option>
            ))}
          </select>
        </label>
      </header>

      {orgId === '' ? (
        <EmptyOrgState />
      ) : (
        // ``key={orgId}`` forces a fresh mount when the selector changes so the
        // panel's local state (last-flip success line, pending target, mutation
        // error/pending) resets per org. Without it, switching orgs after a flip
        // would show the previous org's green "now retell" line — a false
        // confirmation. ``knownProvider`` is keyed by org in the parent, so the
        // badge survives the remount correctly; only the transient panel state
        // needs the reset.
        <OrgProviderPanel
          key={orgId}
          orgId={orgId}
          orgName={selectedOrg?.name ?? orgId}
          // Prefer a flip confirmed THIS session (most current); otherwise
          // fall back to the server-saved provider now surfaced by
          // /orgs-provisioning. Only null if the server somehow omits it.
          knownProvider={
            knownProvider[orgId] ??
            (selectedOrg?.provider as VoiceProvider | undefined) ??
            null
          }
          onProviderConfirmed={(p) =>
            setKnownProvider((prev) => ({ ...prev, [orgId]: p }))
          }
        />
      )}
    </section>
  )
}

function EmptyOrgState() {
  return (
    <div className="flex items-start gap-3 p-4 text-sm text-text-secondary">
      <Info className="mt-0.5 h-4 w-4 flex-shrink-0" />
      <div>
        Pick an org above to view or change its voice provider. Flipping routes
        all <em>new</em> calls through the chosen provider; calls already in
        progress finish on the provider they started on.
      </div>
    </div>
  )
}

interface OrgProviderPanelProps {
  orgId: string
  orgName: string
  // Provider confirmed via a flip this session, or null when we don't yet know.
  knownProvider: VoiceProvider | null
  onProviderConfirmed: (provider: VoiceProvider) => void
}

function OrgProviderPanel({
  orgId,
  orgName,
  knownProvider,
  onProviderConfirmed,
}: OrgProviderPanelProps) {
  const flip = useFlipOrgProvider(orgId)
  const [pendingTarget, setPendingTarget] = useState<VoiceProvider | null>(null)
  // Last successful flip result so we can show a confirmation line.
  const [lastFlip, setLastFlip] = useState<{
    from: VoiceProvider | null
    to: VoiceProvider
  } | null>(null)

  const inFlight = parseInFlightCallsError(flip.error)
  const genericError =
    flip.isError && !inFlight ? getFlipErrorMessage(flip.error) : null

  async function confirmFlip(target: VoiceProvider) {
    try {
      const res = await flip.mutateAsync({ provider: target })
      onProviderConfirmed(res.new_provider)
      setLastFlip({ from: res.previous_provider, to: res.new_provider })
      setPendingTarget(null)
    } catch {
      // surfaced below via flip.error (inFlight / genericError)
    }
  }

  return (
    <div className="space-y-3 p-4">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm text-text-secondary">Current provider</span>
        <ProviderBadge provider={knownProvider} />
        {knownProvider == null && (
          <span className="text-xs text-text-secondary">
            (provider not reported — try reloading)
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <FlipButton
          target="vapi"
          current={knownProvider}
          disabled={flip.isPending}
          onClick={() => setPendingTarget('vapi')}
        />
        <FlipButton
          target="retell"
          current={knownProvider}
          disabled={flip.isPending}
          onClick={() => setPendingTarget('retell')}
        />
      </div>

      {lastFlip && !flip.isError && (
        <div className="flex items-start gap-2 rounded border border-success/30 bg-success/5 p-3 text-xs text-success">
          <Check className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
          <div>
            Provider for <span className="font-medium">{orgName}</span> is now{' '}
            <span className="font-mono">{lastFlip.to}</span>
            {lastFlip.from && lastFlip.from !== lastFlip.to ? (
              <>
                {' '}
                (was <span className="font-mono">{lastFlip.from}</span>)
              </>
            ) : null}
            . New calls route through it; in-flight calls finish on their
            current provider.
          </div>
        </div>
      )}

      {inFlight && (
        <div className="flex items-start gap-2 rounded border border-error/30 bg-error/5 p-3 text-xs text-error">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
          <div>
            Cannot flip — <span className="font-medium">{orgName}</span> has{' '}
            {inFlight.in_flight_count} in-flight call
            {inFlight.in_flight_count === 1 ? '' : 's'}. End them or wait, then
            retry.
            {inFlight.statuses.length > 0 && (
              <div className="mt-1 font-mono text-text-secondary">
                statuses: {inFlight.statuses.join(', ')}
              </div>
            )}
          </div>
        </div>
      )}

      {genericError && (
        <div className="rounded border border-error/30 bg-error/5 p-2 text-xs text-error">
          {genericError}
        </div>
      )}

      {pendingTarget && (
        <ConfirmFlipDialog
          orgName={orgName}
          target={pendingTarget}
          isPending={flip.isPending}
          onConfirm={() => confirmFlip(pendingTarget)}
          onClose={() => setPendingTarget(null)}
        />
      )}
    </div>
  )
}

function ProviderBadge({ provider }: { provider: VoiceProvider | null }) {
  // Reuse the KindChip palette idiom from the phones tab so the two sections
  // read consistently: vapi → primary, retell → success, unknown → neutral.
  const label = provider ?? 'unknown'
  const cls =
    provider === 'vapi'
      ? 'border-primary/40 bg-primary/10 text-primary'
      : provider === 'retell'
        ? 'border-success/40 bg-success/10 text-success'
        : 'border-border bg-background text-text-secondary'
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-mono ${cls}`}
    >
      {label}
    </span>
  )
}

function FlipButton({
  target,
  current,
  disabled,
  onClick,
}: {
  target: VoiceProvider
  current: VoiceProvider | null
  disabled: boolean
  onClick: () => void
}) {
  // Disable the button matching the known-current provider — flipping to the
  // provider you're already on is a backend no-op (still audited), so we hide
  // the foot-gun when we actually know the current value.
  const isCurrent = current === target
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || isCurrent}
      title={isCurrent ? `Already on ${target}` : `Switch to ${target}`}
      className="inline-flex items-center gap-1 rounded border border-border bg-background px-3 py-1.5 text-sm hover:bg-surface-elevated disabled:opacity-50"
    >
      Switch to <span className="font-mono">{target}</span>
    </button>
  )
}

// ─────────────────────────────────────────────────────────────────────────
// Confirmation modal — mirrors AssignPhoneDialog (fixed overlay, no shared
// modal primitive in this codebase).
// ─────────────────────────────────────────────────────────────────────────

function ConfirmFlipDialog({
  orgName,
  target,
  isPending,
  onConfirm,
  onClose,
}: {
  orgName: string
  target: VoiceProvider
  isPending: boolean
  onConfirm: () => void
  onClose: () => void
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md overflow-hidden rounded-lg border border-border bg-surface shadow-xl">
        <header className="flex items-center justify-between border-b border-border px-4 py-3">
          <div className="font-medium">Flip voice provider?</div>
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-border p-1 hover:bg-surface-elevated"
          >
            <X className="h-3 w-3" />
          </button>
        </header>
        <div className="space-y-3 p-4">
          <div className="flex items-start gap-2 rounded border border-warning/30 bg-warning/5 p-3 text-sm">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-warning" />
            <div className="text-text-secondary">
              This routes all <strong>new</strong> calls for{' '}
              <span className="font-medium text-text-primary">{orgName}</span>{' '}
              through <span className="font-mono">{target}</span>. In-flight
              calls finish on their current provider.
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded border border-border bg-background px-3 py-1.5 text-sm hover:bg-surface-elevated"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={isPending}
              className="inline-flex items-center gap-1 rounded bg-primary px-3 py-1.5 text-sm text-white hover:opacity-90 disabled:opacity-50"
            >
              <Check className="h-3.5 w-3.5" />
              {isPending ? 'Flipping…' : `Switch to ${target}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
